import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Modal, Input, Button, Form, Typography, Avatar } from 'antd';
import { LockOutlined, UserOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';

const { Title, Text } = Typography;
const IDLE_TIMEOUT = 15 * 60 * 1000;

interface ScreenLockProps { children: React.ReactNode; }

const ScreenLock: React.FC<ScreenLockProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const [locked, setLocked] = useState(false);
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const lockScreen = useCallback(() => {
    setLocked(true);
    sessionStorage.setItem('nexus_screen_locked', '1');
  }, []);

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(lockScreen, IDLE_TIMEOUT);
  }, [lockScreen]);

  useEffect(() => {
    if (!user) return;
    if (sessionStorage.getItem('nexus_screen_locked') === '1') setLocked(true);
    const events = ['mousedown','mousemove','keydown','scroll','touchstart','click'];
    events.forEach(e => document.addEventListener(e, resetTimer, { passive: true }));
    resetTimer();
    return () => {
      events.forEach(e => document.removeEventListener(e, resetTimer));
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [user, resetTimer]);

  const handleUnlock = async () => {
    if (!pin.trim()) { setError('Please enter your password'); return; }
    setLoading(true); setError('');
    try {
      const tenantId = localStorage.getItem('hr_tenant_id') || 'superadmin';
      // Try admin login first, then employee login
      const loginUrl = 'https://nexus-hr.talenthubupskilling.com/backend/hr-api/auth/admin-login.php';
      const r = await fetch(loginUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_email: user?.email, admin_password: pin, tenant_id: tenantId })
      });
      const d = await r.json();
      if (d.success || d.token || d.access_token || d.hr_token) {
        setLocked(false); setPin('');
        sessionStorage.removeItem('nexus_screen_locked');
        resetTimer();
      } else {
        setError('Incorrect password. Try again.');
      }
    } catch { setError('Connection error. Try again.'); }
    finally { setLoading(false); }
  };

  return (
    <>
      {children}
      <Modal open={locked} footer={null} closable={false} centered width={420}
        styles={{ mask: { backdropFilter: 'blur(8px)', background: 'rgba(0,0,0,0.75)' } }}>
        <div style={{ textAlign: 'center', padding: '16px 0 8px' }}>
          <Avatar size={72} style={{ background: '#1a1a2e', marginBottom: 16 }}
            icon={<LockOutlined style={{ fontSize: 32 }} />} />
          <Title level={4} style={{ margin: '0 0 4px' }}>Screen Locked</Title>
          <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            Locked due to inactivity
          </Text>
          <div style={{ marginBottom: 20, padding: '8px 16px', background: '#f5f5f5', borderRadius: 8, display: 'inline-block' }}>
            <Avatar size={28} icon={<UserOutlined />} style={{ background: '#1890ff', marginRight: 8 }} />
            <Text strong>{user?.email || 'User'}</Text>
          </div>
          <Form onFinish={handleUnlock} style={{ textAlign: 'left' }}>
            <Input.Password size="large" placeholder="Enter your password"
              prefix={<LockOutlined style={{ color: '#999' }} />}
              value={pin} onChange={e => { setPin(e.target.value); setError(''); }}
              style={{ marginBottom: error ? 8 : 16 }} autoFocus />
            {error && <Text type="danger" style={{ display: 'block', marginBottom: 12, fontSize: 13 }}>{error}</Text>}
            <Button type="primary" htmlType="submit" block size="large" loading={loading}
              style={{ background: '#1a1a2e', borderColor: '#1a1a2e', marginBottom: 8 }}>
              Unlock
            </Button>
            <Button block size="large" danger onClick={() => { sessionStorage.removeItem('nexus_screen_locked'); setLocked(false); logout(); }}>
              Sign Out
            </Button>
          </Form>
          <Text type="secondary" style={{ fontSize: 11, marginTop: 16, display: 'block' }}>
            Auto-locks after 15 minutes of inactivity
          </Text>
        </div>
      </Modal>
    </>
  );
};

export default ScreenLock;
